import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import jsPDF from "jspdf";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { getOffer, setOfferVisibility, FILE_URL } from "@/lib/api";
import ShareDialog from "@/components/ShareDialog";
import RefineDialog from "@/components/RefineDialog";
import EmailDialog from "@/components/EmailDialog";
import ValueLift from "@/components/ValueLift";
import OfferConstruction from "@/components/OfferConstruction";
import MoneyModel from "@/components/MoneyModel";
import LeadEngine from "@/components/LeadEngine";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/lib/useBrand";
import {
  ArrowLeft,
  DownloadSimple,
  ShareNetwork,
  TrendUp,
  ShieldCheck,
  Clock,
  Diamond,
  Sparkle,
  Lightning,
  Hourglass,
  Fire,
  ArrowsClockwise,
  EnvelopeSimple,
  EyeSlash,
} from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";

const lever = (Icon, label, body) => ({ Icon, label, body });

function downloadPdfBlob(blob, filename) {
  // Use blob URL + click to bypass iframe / sandbox restrictions
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 1000);
}

async function loadImageAsDataURL(url) {
  try {
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) return null;
    const blob = await r.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(fr.result);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Colour the stack-math note by what the arithmetic actually says.
 *
 * This used to key off `stack_matches_bonuses`, which was true for EVERY
 * over-claim — so a $100 stack printed as $999,999 rendered in the same calm
 * grey as a stack that adds up. That is the one case that loses a buyer who does
 * the sum. Records written before `status` existed fall back to the old boolean
 * rather than being flagged wholesale.
 */
function stackToneClass(sm) {
  const status =
    sm.status || (sm.stack_matches_bonuses ? "matches" : "understated");
  if (status === "matches" || status === "no_bonuses") return "text-[#9AA5B5]";
  if (status === "exceeds_bonuses") return "text-[#C9A961]";
  return "text-[#E0B64B]";
}

export default function OfferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { name: appName } = useBrand();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [hideInput, setHideInput] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);

  useEffect(() => {
    getOffer(id)
      .then((o) => {
        setOffer(o);
        setHideInput(!!o.hide_input_on_share);
      })
      .catch(() => setError("Offer not found"));
  }, [id]);

  const toggleHideInput = async (next) => {
    setSavingVisibility(true);
    setHideInput(next);
    try {
      await setOfferVisibility(id, next);
      toast.success(
        next
          ? "Public link will hide raw inputs"
          : "Public link will show raw inputs"
      );
    } catch {
      setHideInput(!next); // revert
      toast.error("Could not update privacy");
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleExportPdf = async () => {
    if (!offer || exporting) return;
    setExporting(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const margin = 48;
      let y = margin;
      const pageWidth = doc.internal.pageSize.getWidth();
      const lineWidth = pageWidth - margin * 2;

      // Optional user logo at top-left
      if (user?.logo_path) {
        try {
          const dataUrl = await loadImageAsDataURL(FILE_URL(user.logo_path));
          if (dataUrl) {
            const imgType =
              dataUrl.startsWith("data:image/jpeg") ||
              dataUrl.startsWith("data:image/jpg")
                ? "JPEG"
                : "PNG";
            doc.addImage(dataUrl, imgType, margin, margin - 12, 80, 32, undefined, "FAST");
            y = margin + 36;
          }
        } catch {
          /* ignore logo failure */
        }
      }

      const write = (text, opts = {}) => {
        const { size = 11, bold = false, gap = 8, color = [20, 20, 20] } = opts;
        doc.setFont("helvetica", bold ? "bold" : "normal");
        doc.setFontSize(size);
        doc.setTextColor(color[0], color[1], color[2]);
        const lines = doc.splitTextToSize(String(text || ""), lineWidth);
        lines.forEach((line) => {
          if (y > doc.internal.pageSize.getHeight() - margin) {
            doc.addPage();
            y = margin;
          }
          doc.text(line, margin, y);
          y += size + 4;
        });
        y += gap;
      };

      const o = offer.output;
      write(`${appName} — Irresistible Offer`, { size: 10, color: [180, 130, 30] });
      write(o.headline, { size: 20, bold: true, gap: 12 });
      write(o.hook, { size: 12, color: [80, 80, 80], gap: 16 });
      write("SUMMARY", { size: 9, bold: true, color: [180, 130, 30] });
      write(o.summary, { gap: 18 });

      write("VALUE EQUATION", { size: 9, bold: true, color: [180, 130, 30] });
      write(`Dream Outcome: ${o.value_equation.dream_outcome}`);
      write(`Perceived Likelihood: ${o.value_equation.perceived_likelihood}`);
      write(`Time Delay: ${o.value_equation.time_delay}`);
      write(`Effort & Sacrifice: ${o.value_equation.effort_and_sacrifice}`, { gap: 18 });

      if (o.proof && o.proof.moves) {
        write("WHAT ACTUALLY CHANGED", { size: 9, bold: true, color: [180, 130, 30] });
        write(
          `Value score ${o.proof.value_score_before} -> ${o.proof.value_score_after}` +
            (o.proof.multiple ? ` (${o.proof.multiple}x)` : ""),
          { bold: true }
        );
        o.proof.moves.forEach((m) => {
          write(
            `${m.lever.replace(/_/g, " ")}: ${m.before} -> ${m.after} (${m.status})` +
              (m.what_changed ? ` — ${m.what_changed}` : "")
          );
        });
        write(o.proof.note, { gap: 18, color: [80, 80, 80] });
      }

      write("BONUS STACK", { size: 9, bold: true, color: [180, 130, 30] });
      (o.bonus_stack || []).forEach((b, i) => {
        write(`${i + 1}. ${b.name} — ${b.perceived_value}`, { bold: true });
        write(b.description);
      });
      y += 6;

      write("GUARANTEES", { size: 9, bold: true, color: [180, 130, 30] });
      write(`Unconditional: ${o.guarantees.unconditional}`);
      write(`Conditional: ${o.guarantees.conditional}`);
      write(`Anti-Guarantee: ${o.guarantees.anti_guarantee}`, { gap: 16 });

      write("SCARCITY & URGENCY", { size: 9, bold: true, color: [180, 130, 30] });
      write(`Scarcity: ${o.scarcity}`);
      write(`Urgency: ${o.urgency}`, { gap: 16 });

      write("PRICING", { size: 9, bold: true, color: [180, 130, 30] });
      write(`Total stack value: ${o.total_stack_value}`, { bold: true });
      write(`Suggested price: ${o.suggested_price}`, { bold: true, gap: 16 });

      if (o.money_model && (o.money_model.steps || []).length) {
        write("THE MONEY MODEL", { size: 9, bold: true, color: [180, 130, 30] });
        o.money_model.steps.forEach((s) => {
          write(`${s.role.toUpperCase()} — ${s.name} — ${s.price_display} (${s.take_rate_pct}% take)`, {
            bold: true,
          });
          write(s.what_it_is);
        });
        write(
          `First 30 days per buyer: revenue ${o.money_model.revenue_per_buyer_30d}, ` +
            `gross profit ${o.money_model.gross_profit_per_buyer_30d}, ` +
            `max spend to win the next customer ${o.money_model.allowed_cac}`,
          { bold: true }
        );
        write(o.money_model.payback_note, { gap: 16, color: [80, 80, 80] });
      }

      if (o.lead_engine) {
        write("HOW PEOPLE FIND THIS", { size: 9, bold: true, color: [180, 130, 30] });
        const lm = o.lead_engine.lead_magnet || {};
        if (lm.name) {
          write(`Lead magnet: ${lm.name}`, { bold: true });
          write(`Solves completely: ${lm.narrow_problem}`);
          write(`Then reveals: ${lm.reveals_next_problem || "—"}`);
        }
        (o.lead_engine.plays || []).forEach((p) => {
          write(`${p.channel}: ${p.play}`, { bold: true });
          (p.give_give_give || []).forEach((g, gi) => write(`  give ${gi + 1}: ${g}`));
          if (p.ask) write(`  ask: ${p.ask}`);
        });
        y += 6;
      }

      write(`Call to action: ${o.call_to_action}`, {
        size: 13,
        bold: true,
        color: [180, 130, 30],
      });

      // Mandatory disclaimer
      y += 12;
      write(
        `Disclaimer: This document was generated by ${appName}, an independent tool inspired by the "$100M Offers" framework. Not affiliated with, endorsed by, or associated with Alex Hormozi or Acquisition.com.`,
        { size: 8, color: [140, 140, 140], gap: 4 }
      );

      // Generate blob and trigger download via anchor click (works in sandboxed iframes)
      const blob = doc.output("blob");
      const filename = `grand-slam-offer-${offer.id}.pdf`;
      downloadPdfBlob(blob, filename);
      toast.success("PDF downloaded");
    } catch (e) {
      console.error(e);
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8]">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h2 className="font-display text-3xl mb-4">Offer not found</h2>
          <p className="text-[#9AA5B5] mb-8">
            This Irresistible Offer doesn&apos;t exist (or was removed).
          </p>
          <Link to="/" className="text-[#C9A961] hover:underline">
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-[#C9A961] border-t-transparent animate-spin" />
      </div>
    );
  }

  const o = offer.output;
  // Ownership, not "is signed in". /offer/:id is a public share link, so a
  // logged-in stranger following one is still a stranger. The API is the real
  // control — it never sends the seller-only blueprint to a non-owner — and this
  // flag comes from that same decision, so the two cannot drift apart.
  const isOwner = !!offer.viewer_is_owner;
  const levers = [
    lever(TrendUp, "Dream Outcome", o.value_equation.dream_outcome),
    lever(ShieldCheck, "Perceived Likelihood", o.value_equation.perceived_likelihood),
    lever(Clock, "Time Delay", o.value_equation.time_delay),
    lever(Diamond, "Effort & Sacrifice", o.value_equation.effort_and_sacrifice),
  ];

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8] flex flex-col">
      <Navbar />
      <Toaster theme="dark" position="top-right" />

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        url={typeof window !== "undefined" ? window.location.href : ""}
      />
      {isOwner && (
        <RefineDialog
          open={refineOpen}
          onOpenChange={setRefineOpen}
          offerId={offer.id}
        />
      )}
      {isOwner && (
        <EmailDialog
          open={emailOpen}
          onOpenChange={setEmailOpen}
          offerId={offer.id}
          defaultEmail={user.email}
        />
      )}

      <div className="flex-1 max-w-6xl mx-auto w-full px-6 md:px-10 py-12 md:py-16">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-[#9AA5B5] hover:text-white inline-flex items-center gap-2"
            data-testid="offer-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            {isOwner && (
              <Button
                onClick={() => setRefineOpen(true)}
                variant="outline"
                className="bg-transparent border-white/15 text-[#F5F1E8] hover:bg-white/5 hover:border-white/30 rounded-sm"
                data-testid="offer-refine-btn"
              >
                <ArrowsClockwise weight="bold" className="mr-2 h-4 w-4" /> Refine
              </Button>
            )}
            {isOwner && (
              <Button
                onClick={() => setEmailOpen(true)}
                variant="outline"
                className="bg-transparent border-white/15 text-[#F5F1E8] hover:bg-white/5 hover:border-white/30 rounded-sm"
                data-testid="offer-email-btn"
              >
                <EnvelopeSimple weight="bold" className="mr-2 h-4 w-4" /> Email
              </Button>
            )}
            <Button
              onClick={() => setShareOpen(true)}
              variant="outline"
              className="bg-transparent border-white/15 text-[#F5F1E8] hover:bg-white/5 hover:border-white/30 rounded-sm"
              data-testid="offer-share-btn"
            >
              <ShareNetwork weight="bold" className="mr-2 h-4 w-4" /> Share
            </Button>
            <Button
              onClick={handleExportPdf}
              disabled={exporting}
              className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm"
              data-testid="offer-export-btn"
            >
              <DownloadSimple weight="bold" className="mr-2 h-4 w-4" />
              {exporting ? "Generating…" : "Export PDF"}
            </Button>
          </div>
        </div>

        {/* Privacy toggle for offer owner */}
        {isOwner && (
          <div
            className="mb-8 flex items-center justify-between gap-4 bg-[#0F1F38] border border-white/5 rounded-sm px-4 py-3"
            data-testid="offer-privacy-row"
          >
            <div className="flex items-center gap-3">
              <EyeSlash weight="duotone" className="h-5 w-5 text-[#C9A961]" />
              <div>
                <div className="text-sm text-[#F5F1E8] font-medium">
                  Hide raw inputs on public share link
                </div>
                <div className="text-xs text-[#9AA5B5]">
                  When on, anyone with the link sees the polished offer but
                  not your original notes.
                </div>
              </div>
            </div>
            <Switch
              checked={hideInput}
              onCheckedChange={toggleHideInput}
              disabled={savingVisibility}
              data-testid="offer-privacy-switch"
            />
          </div>
        )}

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-4">
            {offer.parent_id ? "Refined Irresistible Offer" : "The Irresistible Offer"}
          </p>
          <h1
            className="font-display text-4xl md:text-6xl font-light tracking-tighter leading-[0.95]"
            data-testid="offer-headline"
          >
            {o.headline}
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-[#F5F1E8] font-body italic" data-testid="offer-hook">
            “{o.hook}”
          </p>
          <p className="mt-6 max-w-3xl text-base text-[#9AA5B5] leading-relaxed">
            {o.summary}
          </p>
          {offer.refine_instructions && (
            <div
              className="mt-6 inline-flex items-start gap-2 bg-[#0F1F38] border border-white/5 rounded-sm px-4 py-3 text-xs text-[#9AA5B5] max-w-2xl"
              data-testid="offer-refine-note"
            >
              <ArrowsClockwise weight="bold" className="h-4 w-4 text-[#C9A961] mt-0.5 shrink-0" />
              <div>
                <span className="text-[#C9A961] font-bold uppercase tracking-[0.2em] mr-2">
                  Refined from:
                </span>
                {offer.refine_instructions}
              </div>
            </div>
          )}
        </motion.div>

        {/* Value Equation - tracing beam */}
        <section className="mt-16">
          <div className="flex items-center gap-3 mb-6">
            <Sparkle weight="fill" className="h-5 w-5 text-[#C9A961]" />
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
              The Value Equation
            </h2>
          </div>
          <div className="grid grid-cols-12 gap-4">
            {levers.map(({ Icon, label, body }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.07 }}
                className="col-span-12 md:col-span-6 bg-[#0F1F38] rounded-lg p-8 tracing-beam relative"
                data-testid={`value-lever-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon weight="duotone" className="h-7 w-7 text-[#C9A961] mb-5" />
                <div className="text-xs tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-2">
                  {label}
                </div>
                <p className="text-[#F5F1E8] leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* v2 blueprint. Each of these renders nothing when the field is absent,
            so offers generated by the old engine are unaffected. */}
        <ValueLift proof={o.proof} />
        {/* Owner only. The construction panel shows what each delivery vehicle
            costs the SELLER and why the others were cut ("too costly") — that is
            the seller's workings, not the buyer's offer. */}
        {isOwner && (
          <OfferConstruction enumeration={o.enumeration} naming={o.naming} />
        )}

        {/* Bonus stack */}
        <section className="mt-20">
          <div className="flex items-center gap-3 mb-6">
            <Lightning weight="fill" className="h-5 w-5 text-[#C9A961]" />
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
              The Bonus Stack
            </h2>
          </div>
          <div className="bg-[#0F1F38] border border-white/5 rounded-lg divide-y divide-white/5">
            {(o.bonus_stack || []).map((b, i) => (
              <div
                key={i}
                className="grid grid-cols-12 gap-6 p-6 md:p-8"
                data-testid={`bonus-row-${i}`}
              >
                <div className="col-span-12 md:col-span-1 text-xs tracking-[0.2em] uppercase text-[#808A9B] font-bold">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-12 md:col-span-8">
                  <div className="font-display font-bold text-lg text-white mb-1">
                    {b.name}
                  </div>
                  <p className="text-sm text-[#9AA5B5] leading-relaxed">
                    {b.description}
                  </p>
                </div>
                <div className="col-span-12 md:col-span-3 md:text-right">
                  <span className="text-xs tracking-[0.2em] uppercase text-[#808A9B] font-bold block mb-1">
                    Perceived value
                  </span>
                  <span className="font-display font-black text-xl gold-text">
                    {b.perceived_value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Guarantees */}
        <section className="mt-20">
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck weight="fill" className="h-5 w-5 text-[#C9A961]" />
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
              The Triple Guarantee
            </h2>
          </div>
          <div className="grid grid-cols-12 gap-4">
            {[
              ["Unconditional", o.guarantees.unconditional, "unconditional"],
              ["Conditional", o.guarantees.conditional, "conditional"],
              ["Anti-Guarantee", o.guarantees.anti_guarantee, "anti-guarantee"],
            ].map(([label, body, key]) => (
              <div
                key={key}
                className="col-span-12 md:col-span-4 bg-[#0F1F38] border border-white/5 rounded-lg p-7"
                data-testid={`guarantee-${key}`}
              >
                <div className="text-xs tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-3">
                  {label}
                </div>
                <p className="text-[#F5F1E8] leading-relaxed text-sm">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Scarcity & urgency */}
        <section className="mt-20 grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6 bg-[#0F1F38] border border-white/5 rounded-lg p-8" data-testid="scarcity-card">
            <Hourglass weight="duotone" className="h-7 w-7 text-[#C9A961] mb-4" />
            <div className="text-xs tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-2">
              Scarcity
            </div>
            <p className="text-[#F5F1E8] leading-relaxed">{o.scarcity}</p>
          </div>
          <div className="col-span-12 md:col-span-6 bg-[#0F1F38] border border-white/5 rounded-lg p-8" data-testid="urgency-card">
            <Fire weight="duotone" className="h-7 w-7 text-[#C9A961] mb-4" />
            <div className="text-xs tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-2">
              Urgency
            </div>
            <p className="text-[#F5F1E8] leading-relaxed">{o.urgency}</p>
          </div>
        </section>

        {/* Pricing anchor */}
        <section className="mt-20">
          <div className="bg-gradient-to-br from-[#0F1F38] via-[#0F1F38] to-[#0F1F38] border border-[#C9A961]/30 rounded-lg p-10 md:p-12 tracing-beam relative">
            <div className="grid grid-cols-12 gap-6 items-center">
              <div className="col-span-12 md:col-span-7">
                <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-3">
                  The anchor
                </p>
                <h3 className="font-display text-3xl md:text-4xl font-light tracking-tight leading-tight">
                  Total stack value{" "}
                  <span className="font-black gold-text">
                    {o.total_stack_value}
                  </span>
                  .
                </h3>
                <p className="mt-4 text-[#9AA5B5]">
                  Suggested investment, anchored against the full stack:
                </p>
              </div>
              <div className="col-span-12 md:col-span-5 md:text-right" data-testid="suggested-price">
                <div className="text-xs tracking-[0.25em] uppercase text-[#808A9B] font-bold mb-2">
                  You pay
                </div>
                <div className="font-display font-black text-4xl md:text-5xl text-white">
                  {o.suggested_price}
                </div>
              </div>
            </div>

            {o.stack_math && (
              <div
                className="mt-8 pt-6 border-t border-white/10 grid grid-cols-12 gap-6 items-center"
                data-testid="stack-math"
              >
                <div className="col-span-6 md:col-span-3">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-1">
                    Bonuses add up to
                  </div>
                  <div className="font-display text-xl text-[#F5F1E8]">
                    {o.stack_math.bonus_total.toLocaleString()}
                  </div>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-1">
                    Anchor multiple
                  </div>
                  <div className="font-display text-xl text-[#C9A961]">
                    {o.stack_math.anchor_multiple}×
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <p
                    className={`text-sm leading-relaxed ${stackToneClass(
                      o.stack_math
                    )}`}
                  >
                    {o.stack_math.note}
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Owner only. The money model is margin: 30-day gross profit per buyer,
            the CAC the seller can afford, per-step COGS and take rates, and a
            downsell step captioned "For the ones who say no". The lead engine is
            the seller's acquisition plan. A prospect following a share link must
            see neither. */}
        {isOwner && <MoneyModel model={o.money_model} />}
        {isOwner && <LeadEngine engine={o.lead_engine} />}

        {/* CTA */}
        <section className="mt-16 text-center">
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-3">
            Your call to action
          </p>
          <p className="font-display text-3xl md:text-4xl font-light tracking-tighter" data-testid="cta-line">
            “<span className="font-black">{o.call_to_action}</span>”
          </p>
        </section>

        {/* Original input */}
        <section className="mt-20 border-t border-white/5 pt-10">
          <p className="text-xs tracking-[0.3em] uppercase text-[#808A9B] font-bold mb-4">
            Forged from
          </p>
          <div className="grid grid-cols-12 gap-6 text-sm text-[#9AA5B5]">
            <div className="col-span-12 md:col-span-6">
              <div className="text-[#F5F1E8] font-bold mb-1">Current offer</div>
              {offer.input.current_offer}
            </div>
            <div className="col-span-6 md:col-span-3">
              <div className="text-[#F5F1E8] font-bold mb-1">Target</div>
              {offer.input.target_customer}
            </div>
            <div className="col-span-6 md:col-span-3">
              <div className="text-[#F5F1E8] font-bold mb-1">Was priced at</div>
              {offer.input.current_price}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
